import React, { useEffect, useState } from 'react';
import { CheckCircle2, FileOutput, Loader2, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { costingImportsAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CostingSheetQuotationDialog } from './CostingSheetQuotationDialog';
import { ExtractionCoverage } from './ExtractionCoverage';
import { WeightChangeAlert } from './WeightChangeAlert';
import { toast } from 'sonner';

const confidenceTone = (value) => {
  if (value >= 0.85) return { className: 'bg-emerald-100 text-emerald-800', label: 'High confidence' };
  if (value >= 0.6) return { className: 'bg-amber-100 text-amber-800', label: 'Check values' };
  return { className: 'bg-red-100 text-red-800', label: 'Low confidence' };
};

const fieldTone = (row, field) => (row.uncertain_fields || []).includes(field)
  ? 'border-amber-500 bg-amber-50'
  : '';

export const CostingSheetReviewDialog = ({ inquiry, record, open, onOpenChange, onRefresh }) => {
  const [rows, setRows] = useState([]);
  const [busyRow, setBusyRow] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [quotationOpen, setQuotationOpen] = useState(false);
  const [rereading, setRereading] = useState(null);

  const discardRow = async (row) => {
    if (!window.confirm(`Discard "${row.customer_part_no || row.description}" from this sheet? No costing is created.`)) return;
    setBusyRow(row.id);
    try {
      await costingImportsAPI.discardRow(record.id, row.id);
      toast.success('Row discarded');
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not discard this row');
    } finally { setBusyRow(null); }
  };

  const readAgain = async (target) => {
    setRereading(target.id);
    try {
      await costingImportsAPI.extract(target.id);
      toast.success('Reading the sheet again — this can take a couple of minutes for long sheets');
      onOpenChange(false);
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start another read');
    } finally {
      setRereading(null);
    }
  };

  useEffect(() => {
    setRows((current) => (record?.rows || []).map((row) => {
      const local = current.find((item) => item.id === row.id);
      if (!local || row.status === 'approved') return { ...row, draft_data: { ...row.draft_data } };
      return { ...row, ...local, status: row.status, draft_data: { ...row.draft_data, ...local.draft_data } };
    }));
  }, [record]);

  if (!record) return null;

  const setRowField = (rowId, field, value) => setRows((current) => current.map((row) => (
    row.id === rowId ? { ...row, [field]: value } : row
  )));
  const setDraftField = (rowId, field, value) => setRows((current) => current.map((row) => (
    row.id === rowId ? { ...row, draft_data: { ...row.draft_data, [field]: value } } : row
  )));
  const setComponentField = (rowId, index, field, value) => setRows((current) => current.map((row) => (
    row.id === rowId
      ? {
        ...row,
        draft_data: {
          ...row.draft_data,
          cost_components: row.draft_data.cost_components.map((component, componentIndex) => (
            componentIndex === index ? { ...component, [field]: value } : component
          )),
        },
      }
      : row
  )));

  const saveRow = async (row, silent = false) => {
    try {
      const response = await costingImportsAPI.updateRow(record.id, row.id, {
        inquiry_product_id: row.inquiry_product_id || undefined,
        create_new_item: Boolean(row.create_new_item),
        part_id: row.part_id || null,
        customer_part_no: row.customer_part_no || undefined,
        description: row.description || undefined,
        draft_data: {
          ...row.draft_data,
          rate_basis: row.rate_basis || row.draft_data.rate_basis,
          quantity: Number(row.draft_data.quantity) || 1,
          total_cost_per_piece: Number(row.draft_data.total_cost_per_piece) || 0,
          total_cost_per_kg: Number(row.draft_data.total_cost_per_kg) || 0,
          margin_percentage: Number(row.draft_data.margin_percentage) || 0,
          selling_price_per_piece: Number(row.draft_data.selling_price_per_piece) || 0,
          selling_price_per_kg: Number(row.draft_data.selling_price_per_kg) || 0,
          net_weight_grams: row.draft_data.net_weight_grams === '' ? null : Number(row.draft_data.net_weight_grams),
          weight_per_piece_kg: null,
          cost_components: (row.draft_data.cost_components || []).map((component) => ({
            ...component, rate: Number(component.rate) || 0,
          })),
        },
      });
      setRows((current) => current.map((item) => (item.id === row.id
        ? { ...response.data, draft_data: { ...response.data.draft_data } } : item)));
      if (!silent) {
        toast.success('Row saved');
        if (onRefresh) await onRefresh();
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save this row');
      return false;
    }
  };

  const approveRow = async (row) => {
    setBusyRow(row.id);
    try {
      if (!(await saveRow(row, true))) return;
      const response = await costingImportsAPI.approveRow(record.id, row.id);
      toast.success(`${response.data.costing_number} created`, {
        description: `Linked to Part Master ${response.data.internal_part_no}.`,
      });
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not approve this row', { duration: 8000 });
    } finally { setBusyRow(null); }
  };

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      for (const row of rows.filter((item) => item.status !== 'approved')) {
        if (!(await saveRow(row, true))) {
          toast.error('Fix the row that failed to save before approving all');
          return;
        }
      }
      const response = await costingImportsAPI.approveAll(record.id);
      if (response.data.approved_count) {
        toast.success(`${response.data.approved_count} costing(s) created`);
      }
      if (response.data.failed_count) {
        toast.error(`${response.data.failed_count} row(s) need attention: ${response.data.failed.map((item) => item.reason).join(' · ')}`, { duration: 10000 });
      }
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approve all failed');
    } finally { setApprovingAll(false); }
  };

  const referenceOnly = record.import_mode === 'reference';
  const approvedCount = rows.filter((row) => row.status === 'approved').length;
  const pendingCount = rows.length - approvedCount;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[94vh] w-[97vw] max-w-[1500px] overflow-hidden p-0" data-testid="costing-sheet-review-dialog">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle className="text-base">{referenceOnly ? 'Cost sheet (reference only)' : 'Review cost sheet'} — {record.filename}</DialogTitle>
            <DialogDescription className="text-xs" data-testid="costing-sheet-review-subtitle">
              {referenceOnly
                ? `${rows.length} row(s) read for your records. This file is stored, viewable and downloadable — no costing is created from it.`
                : `${rows.length} row(s) extracted · ${approvedCount} approved · ${pendingCount} awaiting your approval. Nothing enters the costing module until you approve it. Rows the AI was least sure about are listed first.`}
            </DialogDescription>
            <div className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="costing-sheet-context">
              <Badge variant="outline">Cost basis: {record.rows?.[0]?.rate_basis === 'per_kg' ? 'INR / kg' : 'per piece'}</Badge>
              <Badge variant="outline">Base currency: {record.sheet_currency || 'INR'}</Badge>
              {record.quote_currency && <Badge variant="outline">Quote currency: {record.quote_currency}</Badge>}
              {record.exchange_rate && <Badge variant="outline">FX: {record.exchange_rate} INR / 1 {record.quote_currency || 'USD'}</Badge>}
              {(record.assumptions || []).length > 0 && <Badge variant="outline">{record.assumptions.length} assumption(s) captured</Badge>}
            </div>
            <ExtractionCoverage record={record} rowCount={rows.length} onReread={readAgain} rereading={rereading} />
          </DialogHeader>
          <div className="max-h-[70vh] space-y-4 overflow-auto px-6 py-5">
            {rows.length === 0 && <p className="py-16 text-center text-muted-foreground" data-testid="costing-sheet-rows-empty">No rows were extracted from this sheet.</p>}
            {rows.map((row) => {
              const tone = confidenceTone(row.confidence ?? 0);
              const approved = row.status === 'approved';
              return (
                <div key={row.id} className={`border p-4 ${approved ? 'bg-emerald-50/40' : 'bg-white'}`} data-testid={`costing-sheet-row-${row.id}`}>
                  <div className="mb-3 flex flex-wrap items-center gap-2 border-b pb-3">
                    <span className="font-medium" data-testid={`costing-sheet-row-title-${row.id}`}>Row {(row.source_index ?? 0) + 1}</span>
                    <Badge className={tone.className} data-testid={`costing-sheet-row-confidence-${row.id}`}>
                      {tone.label} · {Math.round((row.confidence ?? 0) * 100)}%
                    </Badge>
                    {approved ? (
                      <Badge className="bg-emerald-600 text-white" data-testid={`costing-sheet-row-status-${row.id}`}>
                        <ShieldCheck className="mr-1 h-3 w-3" /> {row.costing_number}
                      </Badge>
                    ) : <Badge variant="outline" data-testid={`costing-sheet-row-status-${row.id}`}>{referenceOnly ? 'Read for reference' : 'Awaiting approval'}</Badge>}
                    {(row.uncertain_fields || []).length > 0 && !approved && (
                      <span className="border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800" data-testid={`costing-sheet-row-uncertain-${row.id}`}>
                        Check these — AI was unsure: {row.uncertain_fields.join(', ').replaceAll('_', ' ')}
                      </span>
                    )}
                  </div>
                  <div className="grid gap-3 lg:grid-cols-4">
                    <div>
                      <Label className="text-xs">Customer part no</Label>
                      <Input disabled={approved || referenceOnly} value={row.customer_part_no || ''} className={fieldTone(row, 'customer_part_no')}
                        onChange={(event) => setRowField(row.id, 'customer_part_no', event.target.value)}
                        data-testid={`costing-row-part-no-${row.id}`} />
                    </div>
                    <div className="lg:col-span-2">
                      <Label className="text-xs">Description</Label>
                      <Input disabled={approved || referenceOnly} value={row.description || ''} className={fieldTone(row, 'description')}
                        onChange={(event) => setRowField(row.id, 'description', event.target.value)}
                        data-testid={`costing-row-description-${row.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Inquiry item</Label>
                      <select disabled={approved || referenceOnly} className={`mt-1 h-10 w-full border bg-white px-2 text-sm ${!approved && !row.inquiry_product_id && !row.create_new_item ? 'border-amber-500 bg-amber-50' : ''}`}
                        value={row.inquiry_product_id || (row.create_new_item ? '__new__' : '')}
                        onChange={(event) => {
                          const value = event.target.value;
                          const next = {
                            ...row,
                            inquiry_product_id: value === '__new__' ? '' : value,
                            create_new_item: value === '__new__',
                          };
                          setRows((current) => current.map((item) => (item.id === row.id ? next : item)));
                          if (value) saveRow(next, true);
                        }}
                        data-testid={`costing-row-item-select-${row.id}`}>
                        <option value="">— Choose the inquiry item —</option>
                        {(inquiry?.products || []).map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.internal_part_no || product.customer_part_no || 'Unlinked'} — {product.description}
                          </option>
                        ))}
                        <option value="__new__">➕ Create a new inquiry item</option>
                      </select>
                      {!approved && (
                        row.inquiry_product_id ? (
                          <p className="mt-1 text-[11px] text-emerald-700" data-testid={`costing-row-match-${row.id}`}>
                            {row.matched_by === 'manual' ? 'Chosen by you' : `Matched automatically${row.match_score ? ` · ${Math.round(row.match_score * 100)}%` : ''}${row.match_reason ? ` · ${row.match_reason}` : ''}`}
                          </p>
                        ) : row.create_new_item ? (
                          <p className="mt-1 text-[11px] text-amber-800" data-testid={`costing-row-match-${row.id}`}>
                            A NEW inquiry item will be created for this row
                          </p>
                        ) : (
                          <p className="mt-1 text-[11px] font-medium text-amber-800" data-testid={`costing-row-match-${row.id}`}>
                            No matching inquiry item — choose one, or pick “Create a new inquiry item”
                          </p>
                        )
                      )}
                    </div>
                    {row.suggested_part && !approved && (
                      <div className="lg:col-span-2">
                        <Label className="text-xs">Existing Part Master match</Label>
                        <label className="mt-1 flex h-10 cursor-pointer items-center gap-2 border bg-white px-3 text-sm" data-testid={`costing-row-suggested-part-${row.id}`}>
                          <input type="checkbox" disabled={referenceOnly} checked={row.part_id === row.suggested_part.part_id}
                            onChange={(event) => {
                              const partId = event.target.checked ? row.suggested_part.part_id : null;
                              setRowField(row.id, 'part_id', partId);
                              saveRow({ ...row, part_id: partId }, true);
                            }}
                            data-testid={`costing-row-use-suggested-part-${row.id}`} />
                          <span className="truncate">
                            Use {row.suggested_part.internal_part_no} ({Math.round((row.suggested_part.match_score || 0) * 100)}%)
                          </span>
                        </label>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input disabled={approved || referenceOnly} type="number" value={row.draft_data.quantity ?? ''} className={fieldTone(row, 'quantity')}
                        onChange={(event) => setDraftField(row.id, 'quantity', event.target.value)}
                        data-testid={`costing-row-quantity-${row.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Weight (g)</Label>
                      <Input disabled={approved || referenceOnly} type="number" value={row.draft_data.net_weight_grams ?? ''} className={fieldTone(row, 'net_weight_grams')}
                        onChange={(event) => setDraftField(row.id, 'net_weight_grams', event.target.value)}
                        data-testid={`costing-row-weight-${row.id}`} />
                      {!approved && (
                        <WeightChangeAlert
                          alert={row.weight_alert}
                          sheetWeight={row.draft_data.net_weight_grams}
                          testId={`costing-row-weight-alert-${row.id}`}
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">{row.rate_basis === 'per_kg' ? 'Total cost (INR/kg)' : 'Total cost / pc'}</Label>
                      <Input disabled={approved || referenceOnly} type="number" step="0.0001" value={(row.rate_basis === 'per_kg' ? row.draft_data.total_cost_per_kg : row.draft_data.total_cost_per_piece) ?? ''} className={fieldTone(row, 'total_cost_per_piece')}
                        onChange={(event) => setDraftField(row.id, row.rate_basis === 'per_kg' ? 'total_cost_per_kg' : 'total_cost_per_piece', event.target.value)}
                        data-testid={`costing-row-total-cost-${row.id}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Margin %</Label>
                        <Input disabled={approved || referenceOnly} type="number" step="0.01" value={row.draft_data.margin_percentage ?? ''} className={fieldTone(row, 'margin_percentage')}
                          onChange={(event) => setDraftField(row.id, 'margin_percentage', event.target.value)}
                          data-testid={`costing-row-margin-${row.id}`} />
                      </div>
                      <div>
                        <Label className="text-xs">{row.rate_basis === 'per_kg' ? 'SP (INR/kg)' : 'Selling / pc'}</Label>
                        <Input disabled={approved || referenceOnly} type="number" step="0.0001" value={(row.rate_basis === 'per_kg' ? row.draft_data.selling_price_per_kg : row.draft_data.selling_price_per_piece) ?? ''} className={fieldTone(row, 'selling_price_per_piece')}
                          onChange={(event) => setDraftField(row.id, row.rate_basis === 'per_kg' ? 'selling_price_per_kg' : 'selling_price_per_piece', event.target.value)}
                          data-testid={`costing-row-selling-${row.id}`} />
                      </div>
                    </div>
                  </div>
                  {(row.draft_data.cost_components || []).length > 0 && (
                    <div className="mt-4 border" data-testid={`costing-row-components-${row.id}`}>
                      <div className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.8fr] gap-2 border-b bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">
                        <span>Cost head</span><span>Basis</span><span>Rate</span><span>Per piece</span>
                      </div>
                      {row.draft_data.cost_components.map((component, index) => (
                        <div key={component.id} className="grid grid-cols-[1.6fr_0.8fr_0.8fr_0.8fr] items-center gap-2 border-b px-3 py-2 text-sm" data-testid={`costing-row-component-${row.id}-${index}`}>
                          <Input disabled={approved || referenceOnly} value={component.name}
                            onChange={(event) => setComponentField(row.id, index, 'name', event.target.value)}
                            data-testid={`component-name-${row.id}-${index}`} />
                          <span className="text-xs text-muted-foreground">{(component.basis || '').replaceAll('_', ' ')}</span>
                          <Input disabled={approved || referenceOnly} type="number" step="0.0001" value={component.rate ?? ''}
                            onChange={(event) => setComponentField(row.id, index, 'rate', event.target.value)}
                            data-testid={`component-rate-${row.id}-${index}`} />
                          <span className="font-mono text-xs">{Number(component.amount_per_piece || 0).toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!approved && !referenceOnly && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => saveRow(row)} data-testid={`save-costing-row-${row.id}`}>
                        <Save className="mr-2 h-4 w-4" /> Save edits
                      </Button>
                      <Button size="sm" onClick={() => approveRow(row)} disabled={busyRow === row.id} data-testid={`approve-costing-row-${row.id}`}>
                        {busyRow === row.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                        Approve into costing
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                        onClick={() => discardRow(row)} disabled={busyRow === row.id}
                        data-testid={`discard-costing-row-${row.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" /> Discard row
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter className="flex-wrap gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="costing-sheet-close">Close</Button>
            {!referenceOnly && <Button variant="outline" onClick={approveAll} disabled={approvingAll || !pendingCount} data-testid="approve-all-costing-rows">
              {approvingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Approve all rows ({pendingCount})
            </Button>}
            {!referenceOnly && <Button onClick={() => setQuotationOpen(true)} disabled={!approvedCount} data-testid="create-quotation-from-sheet">
              <FileOutput className="mr-2 h-4 w-4" /> Create quotation draft ({approvedCount})
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CostingSheetQuotationDialog record={record} open={quotationOpen} onOpenChange={setQuotationOpen} onRefresh={onRefresh} />
    </>
  );
};
